import { Box, Icon, Image as PlumaImage, PlainButton, Text } from '@customerio/pluma-components/react';
import { Lightbox, useLightbox } from '@/components/overlays/lightbox';
import { useCopilotImageThumbnail } from '../../hooks/use-copilot-image-thumbnail';
import { useCopilotHandleDownload } from '../../hooks/use-copilot-handle-download';
import { getDocumentTypeLabel } from '../../utils/get-document-type-label';
import { getFileIcon } from '../../utils/get-file-icon';
import { FileChipList, type FileData } from './file-chip';
import type { CopilotSession } from '@customerio/hydra-copilot';
import { isEmptyString } from '@/utils/string';
import * as styles from './index.css';

export type { FileData } from './file-chip';

interface AttachmentsProps {
  files: FileData[];
  session?: CopilotSession;
  /** "grid" renders large thumbnail cards (for sent messages). "inline" renders compact chips. */
  variant: 'grid' | 'inline';
  /** Callback when a file is removed — shows close button when provided. */
  onRemove?: (fileId: string) => void;
  /** Only for inline variant — whether chips are read-only. */
  readOnly?: boolean;
}

export function Attachments({ files, session, variant, onRemove, readOnly = true }: AttachmentsProps) {
  const lightbox = useLightbox();

  if (files.length === 0) return null;

  if (variant === 'inline') {
    return <FileChipList files={files} readOnly={readOnly} onRemove={onRemove} session={session} />;
  }

  const imageFiles = files.filter((f) => f.mimeType.startsWith('image/'));
  const documentFiles = files.filter((f) => !f.mimeType.startsWith('image/'));

  return (
    <>
      <Box className={styles.container}>
        {imageFiles.length > 0 ? (
          <Box className={styles.gridContainer}>
            {imageFiles.length % 2 !== 0 ? <Box /> : null}
            {imageFiles.map((file) => (
              <Box key={file.id} className={styles.gridCardWrapper}>
                {onRemove != null ? (
                  <PlainButton
                    className={styles.gridCloseButton}
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                      onRemove(file.id);
                    }}
                    aria-label={`Remove ${file.name}`}
                  >
                    <Icon name="close" size="sm" />
                  </PlainButton>
                ) : null}
                <ImageCard file={file} session={session} onImageClick={lightbox.open} />
              </Box>
            ))}
          </Box>
        ) : null}
        {documentFiles.length > 0 ? (
          <Box display="flex" flexDirection="column" gap="050">
            {documentFiles.map((file) => (
              <DocumentRow key={file.id} file={file} session={session} onRemove={onRemove} />
            ))}
          </Box>
        ) : null}
      </Box>
      <Lightbox src={lightbox.src} onClose={lightbox.close} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Image card (grid thumbnail)
// ---------------------------------------------------------------------------

type ImageCardProps = {
  file: FileData;
  session?: CopilotSession;
  onImageClick: (source: string) => void;
};

function ImageCard({ file, session, onImageClick }: ImageCardProps) {
  const imageUrl = useCopilotImageThumbnail({
    source: file.source,
    isImage: true,
    readOnly: true,
    session,
  });

  if (isEmptyString(imageUrl)) {
    return <Box className={styles.gridCard} backgroundColor="active" />;
  }

  return (
    <PlainButton
      className={styles.gridCard}
      onClick={() => onImageClick(imageUrl)}
      aria-label={file.name}
      cursor="zoom-in"
    >
      <PlumaImage src={imageUrl} alt={file.name} className={styles.gridImage} />
    </PlainButton>
  );
}

// ---------------------------------------------------------------------------
// Document row (full-width, colored icon + name + type label)
// ---------------------------------------------------------------------------

type DocumentRowProps = {
  file: FileData;
  session?: CopilotSession;
  onRemove?: (fileId: string) => void;
};

function DocumentRow({ file, session, onRemove }: DocumentRowProps) {
  const { handleDownload } = useCopilotHandleDownload({
    source: file.source,
    filename: file.name,
    session,
  });

  return (
    <Box className={styles.documentRow}>
      <PlainButton
        display="flex"
        alignItems="center"
        gap="100"
        onClick={handleDownload}
        className={styles.documentRowButton}
      >
        <Box className={styles.documentIconSquare}>
          <Icon name={getFileIcon(file.mimeType)} size="sm" color="subtle" />
        </Box>
        <Box minW="0" flexGrow="1">
          <Text size="sm" weight="medium" truncate title={file.name}>
            {file.name}
          </Text>
          <Text size="xs" color="subtle">
            {getDocumentTypeLabel(file.mimeType)}
          </Text>
        </Box>
      </PlainButton>
      {onRemove != null ? (
        <PlainButton
          className={styles.documentRowClose}
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            onRemove(file.id);
          }}
          aria-label={`Remove ${file.name}`}
        >
          <Icon name="close" size="sm" />
        </PlainButton>
      ) : null}
    </Box>
  );
}
